import clsx from "clsx";
import React from "react";
import ReactDOM from "react-dom";
import Slider, { Settings as SlickSettings } from "react-slick";
import CloseButton from "web/react/components/buttons/close-button/close-button";
import "web/react/components/product-gallery/slick-theme.less";
import "web/react/components/product-gallery/slick.less";
import SVGIcon from "web/react/components/svg-icon/svg-icon";

import {
    Image,
    ProductGalleryImage,
} from "web/react/pages/product/product-image-gallery/product-gallery-image";
import { getSafariMajorVersion } from "web/react/utils/user-agent";
import * as styles from "./product-image-gallery-fullscreen.css";

interface ProductImageGalleryFullscreenProps {
    images: Image[];
    initialSlideIndex?: number;
    onClose: () => void;
}

interface ArrowProps {
    className?: string;
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

function PrevArrow({ className, onClick }: ArrowProps): React.ReactElement {
    return (
        <button
            className={clsx(styles.prevArrow, className)}
            onClick={onClick}
            aria-label="Previous image"
        >
            <SVGIcon name="chevron-thin" rotate={90} />
        </button>
    );
}

function NextArrow({ className, onClick }: ArrowProps): React.ReactElement {
    return (
        <button
            className={clsx(styles.nextArrow, className)}
            onClick={onClick}
            aria-label="Next image"
        >
            <SVGIcon name="chevron-thin" rotate={-90} />
        </button>
    );
}

const slickSettings: SlickSettings = {
    accessibility: true,
    speed: 400,
    slidesToShow: 1,
    centerMode: false,
    infinite: false,
    arrows: true,
    swipe: true,
    touchMove: true,
    draggable: false,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
};

export function ProductImageGalleryFullscreen({
    images,
    initialSlideIndex = 0,
    onClose,
}: ProductImageGalleryFullscreenProps): React.ReactElement {
    const isIOS18 = getSafariMajorVersion() === 18;
    return ReactDOM.createPortal(
        <div className={styles.overlay} data-testid="fullscreen-gallery">
            <span className={styles.closeButton}>
                <CloseButton onClick={onClose} />
            </span>
            <Slider
                {...slickSettings}
                className={styles.slider}
                initialSlide={initialSlideIndex}
                dots={images.length > 1}
            >
                {images.map((image, index) => (
                    <ProductGalleryImage
                        key={`${image.id}-${index}`}
                        src={image.url}
                        alt={image.altText ?? undefined}
                        lazy={!isIOS18}
                        isFullscreen
                        blurImageOnLoad={!isIOS18}
                    />
                ))}
            </Slider>
        </div>,
        document.body
    );
}
